import * as React from 'react';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import useShare from '@/src/Share/components/hooks/useShare';

/*
 * Share journey — engagement rollup for an event's share links: how many
 * unique destinations were shared to, how many opened their gallery, and
 * what they did there (photos viewed, downloads, reshares, CTA clicks).
 * Each destination row expands into its raw event timeline.
 */

const PAGE = 20;

const EVENT_LABELS: Record<string, string> = {
    'page-view': 'Opened gallery',
    'media-view': 'Viewed photo',
    download: 'Downloaded',
    'download-all': 'Downloaded all',
    reshare: 'Reshared',
    'cta-click': 'Clicked CTA',
};

interface ShareJourneySectionProps {
    activationId: number;
}

export default function ShareJourneySection({
    activationId,
}: ShareJourneySectionProps) {
    const {
        getJourneySummary,
        getJourneyDestinations,
        getJourneyEventsForDestination,
    } = useShare();

    const [summary, setSummary] = useState<any>(null);
    const [destinations, setDestinations] = useState<any[]>([]);
    const [limit, setLimit] = useState(PAGE);
    const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
    const [events, setEvents] = useState<any[]>([]);
    const [isLoadingEvents, setIsLoadingEvents] = useState(false);

    useEffect(() => {
        let mounted = true;
        Promise.all([
            getJourneySummary(activationId).catch(() => null),
            getJourneyDestinations(activationId).catch(() => []),
        ]).then(([s, d]) => {
            if (!mounted) return;
            setSummary(s);
            setDestinations(d ?? []);
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const toggleExpand = async (slug: string | null) => {
        if (!slug || expandedSlug === slug) {
            setExpandedSlug(null);
            return;
        }
        setExpandedSlug(slug);
        setIsLoadingEvents(true);
        try {
            setEvents(
                await getJourneyEventsForDestination(activationId, slug),
            );
        } catch {
            setEvents([]);
        } finally {
            setIsLoadingEvents(false);
        }
    };

    if (!summary || summary.uniqueDestinations === 0) {
        return null;
    }

    const openRate = Math.round((summary.openRate ?? 0) * 100);

    const kpis: [string, string | number][] = [
        ['Reached', summary.uniqueDestinations],
        ['Opened', `${summary.openedDestinations} (${openRate}%)`],
        ['Visits', summary.totalVisits],
        ['Photos viewed', summary.mediaViews],
        ['Downloads', summary.downloads],
        ['Reshares', summary.reshares],
        ['CTA clicks', summary.ctaClicks],
    ];

    const th =
        'py-1 pl-3 text-right font-mono text-[9px] font-medium uppercase tracking-[0.1em] text-faint';
    const td =
        'border-t border-line py-[7px] pl-3 text-right tabular-nums text-muted-foreground';

    return (
        <section className="mb-10">
            <span className="ps-label">Share journey</span>

            <div className="mb-1 mt-2.5 flex flex-wrap gap-[22px]">
                {kpis.map(([kpiLabel, value]) => (
                    <div key={kpiLabel}>
                        <div className="text-[20px] font-semibold tracking-[-0.01em] tabular-nums">
                            {typeof value === 'number'
                                ? value.toLocaleString()
                                : value}
                        </div>
                        <div className="mt-px text-[11px] text-muted-foreground">
                            {kpiLabel}
                        </div>
                    </div>
                ))}
            </div>

            <table className="mt-3 w-full border-collapse text-[12px]">
                <thead>
                    <tr>
                        <th className={th + ' pl-0 text-left'}>Destination</th>
                        <th className={th}>Visits</th>
                        <th className={th + ' hidden sm:table-cell'}>
                            Photos viewed
                        </th>
                        <th className={th + ' hidden sm:table-cell'}>
                            Downloads
                        </th>
                        <th className={th + ' hidden sm:table-cell'}>
                            Reshares
                        </th>
                        <th className={th}>Last seen</th>
                    </tr>
                </thead>
                <tbody>
                    {destinations.slice(0, limit).map((d: any, i: number) => (
                        <React.Fragment
                            key={d.personalGallerySlug ?? d.destination ?? i}
                        >
                            <tr
                                onClick={() =>
                                    toggleExpand(d.personalGallerySlug)
                                }
                                className={
                                    d.personalGallerySlug
                                        ? 'cursor-pointer hover:bg-sunk'
                                        : ''
                                }
                            >
                                <td
                                    className={
                                        td +
                                        ' max-w-[220px] truncate pl-0 text-left font-semibold text-foreground'
                                    }
                                >
                                    {d.destination}
                                    <span className="ml-1.5 font-mono text-[9px] font-normal uppercase text-faint">
                                        {d.type === 1 ? 'SMS' : 'Email'}
                                    </span>
                                </td>
                                <td
                                    className={
                                        td +
                                        (Number(d.visits) > 0
                                            ? ' font-semibold text-foreground'
                                            : '')
                                    }
                                >
                                    {d.visits}
                                </td>
                                <td className={td + ' hidden sm:table-cell'}>
                                    {d.mediaViews}
                                </td>
                                <td className={td + ' hidden sm:table-cell'}>
                                    {d.downloads}
                                </td>
                                <td className={td + ' hidden sm:table-cell'}>
                                    {d.reshares}
                                </td>
                                <td className={td}>
                                    {d.lastSeenAt
                                        ? moment(d.lastSeenAt).format(
                                              'MMM D · h:mm A',
                                          )
                                        : '—'}
                                </td>
                            </tr>
                            {expandedSlug === d.personalGallerySlug && (
                                <tr>
                                    <td colSpan={6} className="py-2 pl-4">
                                        {isLoadingEvents && (
                                            <span className="text-[12px] text-faint">
                                                Loading timeline…
                                            </span>
                                        )}
                                        {!isLoadingEvents &&
                                            events.length === 0 && (
                                                <span className="text-[12px] text-faint">
                                                    No activity yet.
                                                </span>
                                            )}
                                        {!isLoadingEvents &&
                                            events.map(
                                                (e: any, ei: number) => (
                                                    <div
                                                        key={e.id ?? ei}
                                                        className="flex items-center gap-3 py-1 text-[12px]"
                                                    >
                                                        <span className="w-[130px] flex-none text-faint">
                                                            {moment(
                                                                e.createdAt,
                                                            ).format(
                                                                'MMM D · h:mm A',
                                                            )}
                                                        </span>
                                                        <span className="text-muted-foreground">
                                                            {EVENT_LABELS[
                                                                e.eventType
                                                            ] ?? e.eventType}
                                                        </span>
                                                    </div>
                                                ),
                                            )}
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            {destinations.length > limit && (
                <div className="flex items-center justify-between border-t border-line py-3">
                    <span className="text-[12px] text-faint">
                        {limit} of {destinations.length}
                    </span>
                    <button
                        type="button"
                        onClick={() => setLimit((l) => l + PAGE)}
                        className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                    >
                        Show more
                    </button>
                </div>
            )}
        </section>
    );
}
